import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";

/**
 * Hook que detecta si el usuario actual es un visitante en modo demo.
 *
 * Fuente de verdad: custom claim `habitia_demo` en el Firebase ID Token
 * (seteado por la callable `solicitarDemo` antes de generar el custom token).
 *
 * Devuelve:
 *   - isDemo: bool             — true si es demo user.
 *   - loading: bool            — true mientras chequea el token la primera vez.
 *   - demoBuildingId: number   — buildingid del edificio demo del usuario.
 *   - demoExpiresAt: Date|null — cuando expira el demo.
 *
 * Refresca cuando cambia el auth state. NO hace polling — los claims solo
 * cambian con un nuevo login (o forceRefresh del token explicito).
 */
const useIsDemo = () => {
  const [state, setState] = useState({
    isDemo: false,
    loading: true,
    demoBuildingId: null,
    demoExpiresAt: null,
  });

  useEffect(() => {
    let mounted = true;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (mounted) {
          setState({
            isDemo: false,
            loading: false,
            demoBuildingId: null,
            demoExpiresAt: null,
          });
        }
        return;
      }
      try {
        const res = await user.getIdTokenResult();
        const claims = res.claims || {};
        if (!mounted) return;
        setState({
          isDemo: claims.habitia_demo === true,
          loading: false,
          demoBuildingId: claims.demo_building_id ?? null,
          demoExpiresAt: claims.demo_expires_at
            ? new Date(claims.demo_expires_at * 1000)
            : null,
        });
      } catch (e) {
        console.warn("useIsDemo: error leyendo claims:", e);
        if (mounted) {
          setState({
            isDemo: false,
            loading: false,
            demoBuildingId: null,
            demoExpiresAt: null,
          });
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  return state;
};

export default useIsDemo;
